import type { AssistantPendingReason, AutonomyLevel } from '@contracts/assistant';
import { formatMoney } from '../../finance/money';

/**
 * Hana's autonomy, as the screens say it (S7 spec §5, mockup §5): the person's card on /assistant/me, the
 * admin's "Tự chủ" tab and the reason line on a confirm card all read these words, so they cannot drift.
 */

export const LEVEL_LABEL: Record<AutonomyLevel, string> = {
  ask: 'Luôn hỏi', light: 'Tự làm việc nhẹ', bounded: 'Tự làm trong giới hạn',
};

/** The two lines under each level card, verbatim from the mockup. */
export const LEVEL_BULLETS: Record<AutonomyLevel, readonly [string, string]> = {
  ask: ['Mọi thao tác ghi đều có thẻ xác nhận, kể cả việc nhẹ.', 'Đọc vẫn chạy luôn.'],
  light: ['Như hiện nay: việc nhẹ chạy luôn, có Hoàn tác.', 'Việc nặng, ghi ra ngoài, tài chính hỏi bạn.'],
  bounded: ['Thêm: tài chính dưới ngưỡng, tool ngoài bạn chọn.',
    'Sau khi đọc email, file: vẫn tự làm việc nhẹ trên thẻ của chính bạn.'],
};

/** An amount in đồng as the settings write it: "5.000.000 ₫". */
export const money = (amount: string): string => `${formatMoney(amount)} ₫`;

export const AUTONOMY_LABELS = {
  title: 'Mức tự chủ',
  description: 'Trợ lý tự làm đến đâu trước khi hỏi bạn.',
  /** The hint of a level above the admin's ceiling, as the server's own refusal words it. */
  locked: (ceiling: AutonomyLevel) => `Admin giới hạn ở "${LEVEL_LABEL[ceiling]}"`,
  financeTitle: 'Khoản tài chính tự ghi tối đa',
  financeHint: (max: string) =>
    `Thêm hoặc sửa khoản thu, chi có số tiền không quá ngưỡng này. Admin cho phép tối đa ${money(max)}.`,
  financeAbove: (max: string) => `Admin giới hạn ở ${money(max)}`,
  toolsTitle: 'Tool ngoài được tự gửi',
  toolsHint: 'Chỉ tool admin cho phép. Không áp dụng khi lượt đã đọc nội dung ngoài hoặc do người khác kích hoạt.',
  toolsNone: 'Admin chưa cho tool ngoài nào tự gửi.',
  alwaysLead: 'Luôn hỏi, ở mọi mức:',
  always: 'xoá; việc chạm tới người khác (giao việc, nhắc hộ, bình luận, chia sẻ); chép dữ liệu sang chỗ người khác '
    + 'thấy; gửi ra ngoài khi lượt đã đọc nội dung ngoài; tài chính trên ngưỡng; vai trò, tài khoản, cài đặt.',
  save: 'Lưu mức tự chủ',
  discard: 'Hủy thay đổi',
  saved: 'Đã lưu cài đặt',
} as const;

/** "Lịch · Tạo sự kiện": the server's name, then the tool's title (its name when it has none). */
export const toolLabel = (t: { serverName: string; tool: string; title: string | null }): string =>
  `${t.serverName} · ${t.title ?? t.tool}`;

/**
 * The line a confirm card says when autonomy made it wait (S7 spec §9.5, D14): one reason, the server's pick. D13: a
 * `tainted` reason is left out when the card already shows S6's taint line ("Hội thoại này có nội dung từ …"). D32: no
 * name to give says "người khác". D36: no reason (the daily self-send cap) is no line. S9 D47: a turn that read a
 * delegation thread names whose comments it read; that line stands beside S6's line (another source).
 */
export function reasonLine(reason: AssistantPendingReason | undefined, taint: readonly string[] | undefined,
  peerTaint?: readonly string[]): string | null {
  if (reason === undefined) return null;
  switch (reason.code) {
    case 'level_ask':
      return `Vì mức ${LEVEL_LABEL.ask}`;
    case 'reach_others': {
      const who = reason.names !== undefined && reason.names.length > 0 ? reason.names.join(', ') : 'người khác';
      return `Việc này chạm tới ${who}. Việc chạm tới người khác luôn hỏi anh, ở mọi mức tự chủ.`;
    }
    case 'over_limit':
      return reason.limit === undefined ? 'Vượt ngưỡng tự ghi' : `Vượt ngưỡng tự ghi ${money(reason.limit)}`;
    case 'tainted':
      if (peerTaint !== undefined && peerTaint.length > 0) return `Lượt này đã đọc bình luận của ${peerTaint.join(', ')}`;
      return taint !== undefined && taint.length > 0 ? null : 'Lượt này đã đọc nội dung ngoài';
    default:
      return null;
  }
}
